import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, FolderOpen, Calendar, User, FileText, Receipt, Search, ChevronLeft, ChevronRight, Truck } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import SearchBar from '../components/Common/SearchBar';
import AlleBestellungen from '../components/Projekt/AlleBestellungen';
import { getAllProjekte, getProjektById } from '../api/projektApi';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  PLANUNG: { label: 'Planung', color: 'bg-gray-100 text-gray-800' },
  IN_BEARBEITUNG: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-800' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-800' },
  ABGERECHNET: { label: 'Abgerechnet', color: 'bg-purple-100 text-purple-800' }
};

const tabs = [
  { id: 'projekte', label: 'Projekte', icon: FolderOpen },
  { id: 'bestellungen', label: 'Bestellungen', icon: Truck }
];

export default function Projekte() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'projekte';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [projekte, setProjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Animation State
  const [clickedCard, setClickedCard] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [transitionData, setTransitionData] = useState(null);
  const cardRefs = useRef({});

  // Prefetch cache for smooth transitions
  const prefetchCache = useRef({});

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 21;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    loadProjekte();
  }, [statusFilter]);

  // Reset auf Seite 1 wenn Suche sich ändert
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const loadProjekte = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await getAllProjekte(params);
      setProjekte(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjekte = projekte.filter(p =>
    p.titel.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.kunde?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination Berechnung
  const totalPages = Math.ceil(filteredProjekte.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjekte = filteredProjekte.slice(startIndex, endIndex);

  // Prefetch project data on hover for smooth transition
  const handleCardHover = async (projektId) => {
    if (!prefetchCache.current[projektId]) {
      try {
        const fullData = await getProjektById(projektId);
        prefetchCache.current[projektId] = fullData;
      } catch (error) {
        // Silently fail - data will be loaded on navigation
      }
    }
  };

  // Handle card click with expand animation
  const handleCardClick = (projekt) => {
    const cardElement = cardRefs.current[projekt.id];
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();

      // Step 1: Card hebt sich an (visual feedback)
      setClickedCard(projekt.id);

      // Get prefetched data if available
      const prefetchedData = prefetchCache.current[projekt.id];

      // Step 2: Sofort Overlay zeigen und Animation starten
      setTimeout(() => {
        setTransitionData({
          projekt: prefetchedData || projekt,
          cardRect: rect
        });
        setShowOverlay(true);

        // Step 3: Schnell zur Detailseite navigieren
        setTimeout(() => {
          navigate(`/projekte/${projekt.id}`);
        }, 250);
      }, 50);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <FolderOpen className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              <h1 className="text-2xl sm:text-3xl font-serif font-bold">Projekte</h1>
            </div>
            {activeTab === 'projekte' && (
              <p className="text-sm text-text-muted mt-1 sm:hidden">
                {filteredProjekte.length} von {projekte.length} Projekte
              </p>
            )}
            {/* Mobile: Button unter Titel */}
            {activeTab === 'projekte' && (
              <button
                onClick={() => navigate('/projekte/neu')}
                className="flex sm:hidden items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition mt-3"
              >
                <Plus className="w-5 h-5" />
                Neues Projekt
              </button>
            )}
          </div>

          {activeTab === 'projekte' && (
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-sm text-text-muted">
                {filteredProjekte.length} von {projekte.length} Projekte
              </div>
              <button
                onClick={() => navigate('/projekte/neu')}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-5 h-5" />
                Neues Projekt
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-surface rounded-card shadow-card mb-6 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide border-b border-border">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'bestellungen' && (
          <AlleBestellungen />
        )}

        {activeTab === 'projekte' && (
          <>
            {/* Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Suchen nach Titel, Nummer oder Kunde..."
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
              >
                <option value="">Alle Status</option>
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Projekte Grid */}
            {filteredProjekte.length === 0 ? (
              <div className="bg-surface rounded-card shadow-card p-8 text-center">
                <FolderOpen className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <p className="text-text-muted">
                  {searchTerm || statusFilter ? 'Keine Projekte gefunden.' : 'Noch keine Projekte vorhanden.'}
                </p>
                {!searchTerm && !statusFilter && (
                  <button
                    onClick={() => navigate('/projekte/neu')}
                    className="mt-4 text-primary hover:underline"
                  >
                    Erstes Projekt erstellen
                  </button>
                )}
              </div>
            ) : (
              <>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  animate={{
                    opacity: showOverlay ? 0 : 1,
                  }}
                  transition={{ duration: 0.25 }}
                >
                  {paginatedProjekte.map((projekt) => (
                    <motion.div
                      key={projekt.id}
                      ref={(el) => cardRefs.current[projekt.id] = el}
                      onClick={() => handleCardClick(projekt)}
                      onHoverStart={() => handleCardHover(projekt.id)}
                      className={`bg-surface rounded-card shadow-card p-6 cursor-pointer ${
                        clickedCard === projekt.id ? 'invisible' : ''
                      }`}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      }}
                      whileTap={{
                        scale: 0.98,
                      }}
                      animate={{
                        scale: clickedCard === projekt.id ? 1.05 : 1,
                        boxShadow: clickedCard === projekt.id
                          ? '0 20px 60px rgba(0,0,0,0.2)'
                          : '0 4px 20px rgba(0,0,0,0.08)',
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 25,
                      }}
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <FolderOpen className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs text-text-muted font-mono block">{projekt.nummer}</span>
                            <h3 className="text-lg font-semibold truncate">{projekt.titel}</h3>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap ${STATUS_CONFIG[projekt.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {STATUS_CONFIG[projekt.status]?.label || projekt.status}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-text-muted">
                          <User className="w-4 h-4" />
                          <span className="truncate">{projekt.kunde?.name || 'Kein Kunde'}</span>
                        </div>

                        {projekt.startDatum && (
                          <div className="flex items-center gap-2 text-sm text-text-muted">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(projekt.startDatum).toLocaleDateString('de-DE')}
                              {projekt.endDatum && ` - ${new Date(projekt.endDatum).toLocaleDateString('de-DE')}`}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-text-muted">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {projekt._count?.anfragen || 0} Anfragen
                        </span>
                        <span className="flex items-center gap-1">
                          <Receipt className="w-3 h-3" />
                          {projekt._count?.rechnungen || 0} Rechnungen
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 bg-surface rounded-card shadow-card px-4 sm:px-6 py-4">
                    <div className="text-sm text-text-muted">
                      Zeige {startIndex + 1} - {Math.min(endIndex, filteredProjekte.length)} von {filteredProjekte.length} Projekte
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Zurück</span>
                      </button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                          })
                          .map((page, idx, arr) => (
                            <span key={page} className="flex items-center">
                              {idx > 0 && arr[idx - 1] !== page - 1 && (
                                <span className="px-1 sm:px-2 text-text-muted">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`px-2 sm:px-3 py-2 text-sm rounded-button transition ${
                                  currentPage === page
                                    ? 'bg-primary text-white'
                                    : 'hover:bg-gray-50 border border-border'
                                }`}
                              >
                                {page}
                              </button>
                            </span>
                          ))}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <span className="hidden sm:inline">Weiter</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Expand Animation Overlay */}
            <AnimatePresence>
              {showOverlay && transitionData && (
                <motion.div
                  className="fixed inset-0 z-50 pointer-events-none"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Background */}
                  <motion.div
                    className="absolute inset-0 bg-background"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  />

                  {/* Expanding Card */}
                  <motion.div
                    className="absolute bg-surface rounded-card shadow-2xl overflow-hidden"
                    initial={{
                      top: transitionData.cardRect.top,
                      left: transitionData.cardRect.left,
                      width: transitionData.cardRect.width,
                      height: transitionData.cardRect.height,
                      borderRadius: 16,
                    }}
                    animate={{
                      top: 380,
                      left: '50%',
                      x: '-50%',
                      width: 'min(calc(100vw - 32px), 1200px)',
                      height: 'auto',
                      borderRadius: 16,
                    }}
                    transition={{
                      duration: 0.25,
                      ease: [0.32, 0.72, 0, 1],
                    }}
                  >
                    <div className="p-4 sm:p-6" style={{ minHeight: '140px' }}>
                      {/* Header mit Titel und Status */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className="text-sm text-text-muted font-mono">{transitionData.projekt.nummer}</span>
                          <h1 className="text-xl sm:text-3xl font-serif font-bold">
                            {transitionData.projekt.titel}
                          </h1>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_CONFIG[transitionData.projekt.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                          {STATUS_CONFIG[transitionData.projekt.status]?.label || transitionData.projekt.status}
                        </span>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 text-text-muted">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{transitionData.projekt.kunde?.name || 'Kein Kunde'}</span>
                        </div>
                        {transitionData.projekt.startDatum && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(transitionData.projekt.startDatum).toLocaleDateString('de-DE')}
                              {transitionData.projekt.endDatum && ` - ${new Date(transitionData.projekt.endDatum).toLocaleDateString('de-DE')}`}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Beschreibung falls vorhanden */}
                      {transitionData.projekt.beschreibung && (
                        <motion.div
                          className="mt-4 pt-4 border-t border-border"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2, duration: 0.2 }}
                        >
                          <h3 className="font-semibold mb-2">Beschreibung</h3>
                          <p className="text-text-muted whitespace-pre-wrap">{transitionData.projekt.beschreibung}</p>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </Layout>
  );
}
